import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    Button,
    Input,
    Table,
    Card,
    Tag,
    Row,
    Col,
    Avatar,
    Tooltip
} from 'antd';
import Modal from '../../common/model';
import TextBase from '../../common/text';
import Search from '../../common/search';
import {
    EditOutlined,
    DeleteOutlined,
    SafetyCertificateOutlined,
    CheckOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { MESSAGE_TYPE, messageManager, translate } from '../../helper';
import { Colors } from '../../themes/Colors';
import { useSelector } from 'react-redux';
import LocalStorage from '../../defined/localStorage';
import { Box, Button as MuiButton, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, styled } from '@mui/material';
import { ReactComponent as CreatePlusIcon } from '../../assets/image/svg/create-plus.svg';
import { ArrowDropDown as ArrowDropDownIcon, ManageAccountsOutlined, AdminPanelSettingsOutlined, SecurityOutlined } from '@mui/icons-material';
import TextField from '../../common/form/TextInput';
import Utils from '../../utils';
import CustomSpinner from '../../common/CustomSpinner';
import PermissionSelector from './PermissionSelector';
import usePagination from '../../common/table/Pagination';
import ConfirmDialog from '../../common/confirm-modal/ConfirmDialog';
import PageTitleHeader from '../../common/page-title-header';

/* ──────────────────────────────────────────────
   Constants & Configuration
   ────────────────────────────────────────────── */

const ROLE_SORT_OPTIONS = [
    { label: 'All Roles', value: 'all' },
    { label: 'Active Only', value: 'active' },
    { label: 'Inactive Only', value: 'inactive' }
];

const PERMISSIONS_PAGE_SIZE = 20;

/* ──────────────────────────────────────────────
   Reusable Style Constants
   ────────────────────────────────────────────── */

/** Base pill-shaped tag style (used for Users, Permissions, Status columns) */
const pillTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    height: 'calc(var(--fs-xl)*1.4)',
    fontSize: 'calc(var(--fs-xs)*1.32075)',
    lineHeight: 'calc(var(--fs-xs)*0.09433)',
    whiteSpace: 'nowrap',
};

const RoleBox = styled('div')(({ theme }) => ({
  backgroundColor: '#ffffff',
  backgroundImage: 'linear-gradient(135deg, #f5f7fa 0%, #f9fafb 100%)',
  overflowY: 'auto',
  padding: 'calc(var(--sp-lg)*1.5) calc(var(--sp-lg)*1.6) calc(var(--sp-md2)*1)',
  '& .ant-table-container': {
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    '@media (max-width: 1200px)': {
      overflowX: 'auto'
    }
  },
  '& .ant-table': {
    backgroundColor: '#ffffff',
    borderRadius: 'calc(var(--sp-md2)*1.50943)',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(229, 231, 235, 0.6)',
    borderCollapse: 'collapse',
    '&:hover': {
      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.04)'
    }
  },
  '& .ant-table-thead > tr': {
    backgroundColor: 'linear-gradient(135deg, #1A5A78 0%, #0F3D52 100%)',
    borderRight: 'none !important',
    borderLeft: 'none !important',
    borderTop: 'none !important',
  },
  '& .ant-table-thead > tr > th': {
    backgroundColor: '#1A5A78 !important',
    color: '#ffffff !important',
    borderBottom: '2px solid #0F3D52 !important',
    borderRight: 'none !important',
    borderLeft: 'none !important',
    borderTop: 'none !important',
    transition: 'all 0.3s ease',
    letterSpacing: '0.035em',
    boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.3), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
  },
  '& .ant-table-tbody > tr': {
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    borderBottom: '1px solid rgba(229, 231, 235, 0.7) !important',
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor: 'linear-gradient(90deg, rgba(26, 90, 120, 0.06) 0%, rgba(26, 90, 120, 0.02) 100%) !important',
      boxShadow: `
      0 12px 28px rgba(15, 23, 42, 0.10),
      0 2px 8px rgba(15, 23, 42, 0.06)
    `,
    zIndex: 2,

    background:
      'linear-gradient(90deg, rgba(26,90,120,0.04) 0%, rgba(255,255,255,1) 50%, rgba(26,90,120,0.04) 100%)',
      transform: 'translateY(-3px)',
    },
    '&:last-child': {
      borderBottom: 'none !important'
    }
  },
  '& .ant-table tbody tr': {
    border: 'none !important'
  },
  '& .ant-table-tbody > tr > td': {
    color: '#1f2937',
    transition: 'all 0.2s ease',
    borderBottom: '1px solid rgba(229, 231, 235, 0.8) !important',
    borderRight: 'none !important',
    borderLeft: 'none !important',
    borderTop: 'none !important',
    backgroundColor: '#ffffff'
  },
  '& .ant-table-tbody > tr:last-child > td': {
    borderBottom: 'none !important'
  },
  '& .ant-table-tbody > tr:hover > td': {
    backgroundColor: 'rgba(26, 90, 120, 0.03) !important',
    borderTop: '1px solid rgba(26,90,120,0.08)',
    borderBottom: '1px solid rgba(26,90,120,0.08)',
  },
  '& .MuiOutlinedInput-input': {
    padding: 'var(--sp-md2) calc(var(--sp-md2)*1.4)'
  },
  '& .MuiButton-root': {
    backgroundColor: '#ffffff !important',
    color: '#374151 !important',
    border: '1.5px solid #e5e7eb !important',
    borderRadius: 'var(--sp-md2)',
    fontWeight: '500',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
    fontSize: 'calc(var(--fs-xs) * 1.5)',
    '&:hover': {
      backgroundColor: '#f9fafb !important',
      borderColor: '#d1d5db !important',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  },
  '& .MuiButtonGroup-root': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04) !important',
    height: 'calc(var(--fs-xl)*2.35)',
    borderRadius: 'var(--sp-md2)',
    border: '1.5px solid #e5e7eb !important',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease'
  },
}));

/** Shared icon-action button style (Edit / Permissions / Delete) */
const actionBtnStyle = {
    width: 'calc(var(--fs-xl)*1.6)',
    height: 'calc(var(--fs-xl)*1.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
};

/** Shared tooltip popover style */
const tooltipOverlayStyle = {
    fontSize: 'calc(var(--fs-xs)*1.2)',
    padding: 'var(--sp-sm) var(--sp-md2)',
};

/** Action icon font size */
const ACTION_ICON_SIZE = 'calc(var(--fs-xs)*1.886792)';

/** Shared inline-loading indicator style */
const loadingContainerStyle = {
    textAlign: 'center',
    padding: 'calc(var(--sp-lg)*1.886792)',
    fontSize: 'calc(var(--fs-sm2)*1.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'calc(var(--sp-md2)*1.132075)',
};

/** Cancel button style (used in both modals) */
const cancelBtnStyle = {
    borderRadius: 'calc(var(--sp-sm)*1.132075)',
    marginRight: 'calc(var(--sp-xxs)*2)',
    fontSize: 'calc(var(--fs-xs)*1.50943)',
    padding: 'calc(var(--sp-md2)*1.8) calc(var(--sp-sm)*2)',
};

/** Primary/gradient button builder (used for Save / Create) */
const getGradientBtnStyle = (color, disabled) => ({
    borderRadius: 'calc(var(--sp-sm)*1.132075)',
    fontWeight: 600,
    fontSize: 'calc(var(--fs-xs)*1.50943)',
    padding: 'calc(var(--sp-md2)*1.8) calc(var(--sp-sm)*2.5)',
    background: disabled
        ? '#d9d9d9'
        : `linear-gradient(222.6deg, ${color} 0%, ${color} 100%)`,
    color: disabled ? '#8c8c8c' : 'white',
    border: 'none',
    boxShadow: disabled ? 'none' : '0px 6px 16px 0px #f3f1ed40',
    cursor: disabled ? 'not-allowed' : 'pointer',
});

/** Permission scroll container style */
const permissionScrollStyle = {
    marginTop: 'calc(var(--sp-md2)*1.50943)',
    maxHeight: 'calc(var(--fs-hero)*9.43396)',
    overflowY: 'auto',
};

/** Circular inline-edit button base */
const circleButtonBase = {
    width: 'calc(var(--fs-xl)*1.50943)',
    height: 'calc(var(--fs-xl)*1.50943)',
    minWidth: 'calc(var(--fs-xl)*1.50943)',
    minHeight: 'calc(var(--fs-xl)*1.50943)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'pointer',
};

/* ──────────────────────────────────────────────
   Reusable Small Components
   ────────────────────────────────────────────── */

/** Loading spinner with optional text – eliminates 4x duplication */
const LoadingSpinner = ({ size = 'calc(var(--fs-xl)*1.886792)', text = 'Loading permissions...' }) => (
    <div style={loadingContainerStyle}>
        <CustomSpinner size={size} />
        <span>{text}</span>
    </div>
);

/** Permission list with scroll & loading indicator – used in both modals */
const PermissionScrollContent = ({
    isLoading,
    permissionsList,
    value,
    onChange,
    permissionLoading,
    onScroll,
    title = 'Select permissions for this role',
    titleMarginBottom = 'calc(var(--sp-md2)*1.50943)',
}) => (
    <div style={permissionScrollStyle} onScroll={onScroll}>
        <TextBase
            style={{ marginBottom: titleMarginBottom }}
            size={'calc(var(--fs-xs)*1.6)'}
            color={'#1A2232'}
            fontWeight={500}
        >
            {title}
        </TextBase>
        {isLoading ? (
            <LoadingSpinner />
        ) : (
            <>
                <PermissionSelector
                    permissionsList={permissionsList}
                    value={value}
                    onChange={onChange}
                />
                {permissionLoading && (
                    <LoadingSpinner size="calc(var(--fs-xl)*1.32075)" text="Loading more permissions..." />
                )}
            </>
        )}
    </div>
);

/** Modal footer with Cancel + Primary action button */
const ModalActionFooter = ({ onCancel, onSubmit, loading, disabled, buttonColor, submitText }) => (
    <div style={{ marginTop: 'calc(var(--sp-lg)*1.132075)', textAlign: 'right' }}>
        <Button onClick={onCancel} style={cancelBtnStyle}>
            Cancel
        </Button>
        <Button
            type="primary"
            onClick={onSubmit}
            loading={loading}
            disabled={disabled}
            style={getGradientBtnStyle(buttonColor, disabled)}
        >
            {submitText}
        </Button>
    </div>
);

/* ──────────────────────────────────────────────
   Styled Components
   ────────────────────────────────────────────── */

const Label = styled('label')(({ theme }) => ({
    'fontSize': 'calc(var(--fs-xs)*1.6)',
    'fontWeight': '500',
    'marginBottom': 'var(--sp-xs)',
    'color': '#000',
    'lineHeight': 'normal',
    'maxWidth': 'calc(var(--fs-hero)*6.75)',
    'flex': '0 0 calc(var(--fs-hero)*6.75)',
}));

const brand = JSON.parse(LocalStorage.getItem('branding'))

const CreateButton = styled(Button)(({ buttonColor }) => {
    return {
        background: `linear-gradient(222.6deg, ${buttonColor} 0%, ${buttonColor} 100%) !important`,
        borderRadius: 'var(--sp-xs)',
        color: 'white !important',
        padding: '0 calc(var(--fs-xl)*1)',
        marginLeft: 'var(--sp-md2)',
        fontWeight: '700',
        fontSize: 'var(--fs-md2)',
        border: '0 !important',
        outline: 'none',
        height: 'calc(var(--fs-xl)*2.35)',
        boxShadow: 'none',

        /* ✅ OVERRIDE ALL STATES */
        '&:hover, &:focus, &:active': {
            background: `linear-gradient(222.6deg, ${buttonColor} 0%, ${buttonColor} 100%) !important`,
            color: 'white !important',
            boxShadow: '0px 6px 16px 0px #f3f1ed40',
            border: '0 !important'
        }
    };
});

const RoleManagement = () => {
    /* ── Roles State ── */
    const [roles, setRoles] = useState([]);
    const [totalRoles, setTotalRoles] = useState(0);
    const [isRoleLoading, setIsRoleLoading] = useState(false);
    const [filters, setFilters] = useState({ pageNo: 1, limit: 10 });

    /* ── Create Role State ── */
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [newRolePermissions, setNewRolePermissions] = useState([]);
    const [creatingRole, setCreatingRole] = useState(false);

    /* ── Edit Role State ── */
    const [editingRole, setEditingRole] = useState(null);
    const [editRoleName, setEditRoleName] = useState('');
    const [updatingRoleId, setUpdatingRoleId] = useState(null);

    /* ── Delete Role State ── */
    const [deletingRoleId, setDeletingRoleId] = useState(null);
    const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);

    /* ── Permissions State ── */
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [permissionsList, setPermissionsList] = useState([]);
    const [permissionNext, setPermissionNext] = useState(null);
    const [permissionLoading, setPermissionLoading] = useState(false);
    const [permissionPage, setPermissionPage] = useState(1);
    const [totalPermissions, setTotalPermissions] = useState(0);
    const [savingPermissions, setSavingPermissions] = useState(false);
    const [roleDetailLoading, setRoleDetailLoading] = useState(false);

    /* ── UI State ── */
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openSortByMenu, setOpenSortByMenu] = useState(false);

    const anchorRef = useRef(null);

    /* ── Branding / Theme ── */
    const whiteLabel = useSelector((state) => state.User?.branding);
    const buttonColor =
        brand?.web_button_color ||
        whiteLabel?.web_button_color ||
        '#F8B218';

    /** Generate a deterministic HSL color from an id */
    const getColorFromId = useCallback(
        (id) => `hsl(${(id * 137.5) % 360}, 70%, 45%)`,
        []
    );

    const [paging] = usePagination(totalRoles, filters, setFilters, isRoleLoading);

    /* ──────────────────────────────────────────────
       API Calls
       ────────────────────────────────────────────── */

    /** Fetch permissions with scroll-pagination */
    const getAllPermissions = (page = 1) => {
        if (permissionLoading) return;

        setPermissionLoading(true);

        Utils.api.getApiCall(
            `groups/permissions/`,
            `?page=${page}&page_size=${PERMISSIONS_PAGE_SIZE}`,
            (respData) => {
                const data = respData?.data;

                setPermissionsList(prev => {
                    const newItems = data.results.filter(
                        item => !prev.some(p => p.id === item.id)
                    );
                    return [...prev, ...newItems];
                });
                if (page === 1) setTotalPermissions(data.count || 0);
                setPermissionNext(data.next);
                setPermissionPage(page);
                setPermissionLoading(false);
            },
            (error) => {
                setPermissionLoading(false);
                Utils.showAlert(2, error.detail || error.message);
            }
        );
    };

    /** Fetch roles list (paginated) */
    const getGroupsList = (page = filters.pageNo, limit = filters.limit) => {
        setIsRoleLoading(true);

        Utils.api.getApiCall(
            'groups/',
            `?page=${page}&page_size=${limit}`,
            (respData) => {
                const data = respData?.data;

                // handle paginated or normal response
                const groups = data?.results || data || [];
                const mappedRoles = groups.map(group => ({
                    id: group.id,
                    name: group.name,
                    permissions: group.permissions || [],
                    users: group.users_count || 0,
                    color: getColorFromId(group.id),
                    is_system: group.is_system || false,
                }));

                setRoles(mappedRoles);
                setTotalRoles(data?.count || groups.length);
                setIsRoleLoading(false);
            },
            (error) => {
                Utils.showAlert(2, error?.message || "Failed to load roles");
                setIsRoleLoading(false);
            }
        );
    };

    /** Update a role (name or permissions) via PATCH */
    const updateRoleApi = (roleId, payload, onSuccess) => {

        setUpdatingRoleId(roleId);

        Utils.api.patchApiCall(
            `groups/${roleId}/`,
            payload,

            () => {
                messageManager({
                    type: MESSAGE_TYPE.success,
                    messageShow: "Role updated successfully"
                });

                setUpdatingRoleId(null);
                getGroupsList();
                onSuccess?.();
            },

            (error) => {
                setUpdatingRoleId(null);

                messageManager({
                    type: MESSAGE_TYPE.error,
                    messageShow:
                        error?.data?.message ||
                        "Failed to update role"
                });
            }
        );
    };

    /** Fetch single role detail (used before opening permission modal) */
    const getRoleDetail = (roleId, callback) => {
        Utils.api.getApiCall(
            `groups/${roleId}/`,
            '',
            (res) => callback(res.data),
            (err) => {
                Utils.showAlert(2, err?.message || "Failed to load role");
            }
        );
    };

    /** Delete a role and close the confirm modal on completion */
    const deleteRoleApi = (roleId) => {

        setDeletingRoleId(roleId);

        Utils.api.deleteApiCall(
            `groups/${roleId}/`,
            {},

            () => {
                messageManager({
                    type: MESSAGE_TYPE.success,
                    messageShow: "Role deleted successfully"
                });

                setDeletingRoleId(null);
                setShowDeleteRoleModal(false);
                setRoleToDelete(null);
                getGroupsList();
            },

            () => {
                setDeletingRoleId(null);
                setShowDeleteRoleModal(false);
                setRoleToDelete(null);
            }
        );
    };

    /* ── Effects ── */

    useEffect(() => {
        getAllPermissions(1);
    }, []);

    useEffect(() => {
        getGroupsList(filters.pageNo, filters.limit);
    }, [filters.pageNo, filters.limit]);

    /* ──────────────────────────────────────────────
       Event Handlers
       ────────────────────────────────────────────── */

    /** Load next page of permissions when user scrolls near bottom */
    const handlePermissionScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

        if (isNearBottom && permissionNext && !permissionLoading) {
            getAllPermissions(permissionPage + 1);
        }
    };

    /** Create a new role with name + permissions */
    const handleCreateRoleFromModal = () => {

        if (creatingRole) return;

        const roleName = newRole.trim();
        if (!roleName) return;

        if (isDuplicateRoleName(roleName)) {
            messageManager({
                type: MESSAGE_TYPE.error,
                messageShow: 'Role already exists'
            });
            return;
        }

        setCreatingRole(true);

        const payload = {
            name: roleName,
            permissions: newRolePermissions
        };

        Utils.api.postApiCall(
            "groups/",
            payload,

            // ✅ SUCCESS
            () => {
                messageManager({
                    type: MESSAGE_TYPE.success,
                    messageShow: "Role created successfully"
                });

                getGroupsList();

                setCreateModalVisible(false);
                setNewRole('');
                setNewRolePermissions([]);
                setCreatingRole(false);
            },

            // ❌ ERROR
            (error) => {
                messageManager({
                    type: MESSAGE_TYPE.error,
                    messageShow:
                        error?.data?.message || error?.data?.name?.[0] || "Failed to create role"
                });

                setCreatingRole(false);
            }
        );
    };

    /** Enter inline-editing mode for a role name */
    const handleEditRole = (role) => {
        setEditingRole(role);
        setEditRoleName(role.name);
    };

    const handleSaveEditRole = () => {
        const roleName = editRoleName.trim();
        if (!roleName) return;

        if (isDuplicateRoleName(roleName, editingRole.id)) {
            messageManager({
                type: MESSAGE_TYPE.error,
                messageShow: 'Role already exists'
            });
            return;
        }

        updateRoleApi(
            editingRole.id,
            {
                name: roleName,
                permissions: editingRole.permissions.map(p => p.id)
            },
            () => {
                setEditingRole(null);
                setEditRoleName('');
                getGroupsList();
            }
        );
    };

    /** Trigger role deletion */
    const handleDeleteRole = (role) => deleteRoleApi(role.id);

    /** Open permission assignment modal & fetch role details */
    const openAssignModal = (role) => {

        setRoleDetailLoading(true);
        setAssignModalVisible(true);

        getRoleDetail(role.id, (roleDetail) => {

            setSelectedRole({
                id: roleDetail.id,
                name: roleDetail.name
            });

            setSelectedPermissions(
                (roleDetail.permissions || []).map(p => p.id)
            );

            setRoleDetailLoading(false);
        });
    };

    const handleSavePermissions = () => {
        if (!selectedRole) return;

        if (selectedPermissions.length === 0) {
            messageManager({
                type: MESSAGE_TYPE.error,
                messageShow: "At least one permission is required"
            });
            return;
        }

        setSavingPermissions(true);

        updateRoleApi(
            selectedRole.id,
            {
                name: selectedRole.name,
                permissions: selectedPermissions
            },
            () => {
                setSavingPermissions(false);
                setAssignModalVisible(false);
                setSelectedRole(null);
                setSelectedPermissions([]);
            }
        );
    };

    /* ──────────────────────────────────────────────
       Derived / Computed Values
       ────────────────────────────────────────────── */

    /** Roles filtered by search text + status filter */
    const filteredRoles = useMemo(() => {
        return roles.filter(role => {
            const matchesSearch = role.name.toLowerCase().includes(searchText.toLowerCase());
            const isActive = role.users > 0;
            const matchesStatus =
                statusFilter === 'all' ||
                (statusFilter === 'active' && isActive) ||
                (statusFilter === 'inactive' && !isActive);
            return matchesSearch && matchesStatus;
        });
    }, [roles, searchText, statusFilter]);

    /** Check if role name already exists (case-insensitive) */
    const isDuplicateRoleName = useCallback((name, ignoreId = null) => {
        return roles.some(role =>
            role.name.toLowerCase() === name.toLowerCase() &&
            role.id !== ignoreId
        );
    }, [roles]);

    /* ──────────────────────────────────────────────
       Table Column Definitions
       ────────────────────────────────────────────── */

    const columns = [
        /* ── ROLE column (with inline edit) ── */
        {
            title: 'ROLE',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            render: (text, role) =>
                editingRole?.id === role.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'calc(var(--sp-md2)*1.2)' }}>
                        <Input
                            value={editRoleName}
                            onChange={(e) => setEditRoleName(e.target.value)}
                            onPressEnter={handleSaveEditRole}
                            autoFocus
                            style={{
                                height: 'calc(var(--fs-xl)*1.50943)',
                                borderRadius: 'calc(var(--sp-xxs)*2)',
                                fontSize: 'calc(var(--fs-xs)*1.50943)',
                                padding: '0 calc(var(--sp-md2))',
                                minWidth: 'calc(var(--fs-hero)*4.245)',
                            }}
                        />
                        {/* Save */}
                        <div
                            style={{ ...circleButtonBase, backgroundColor: '#4f46e5' }}
                            onClick={handleSaveEditRole}
                        >
                            <CheckOutlined
                                style={{
                                    fontSize: ACTION_ICON_SIZE,
                                    color: '#fff',
                                    pointerEvents: updatingRoleId === role.id ? 'none' : 'auto',
                                    opacity: updatingRoleId === role.id ? 0.6 : 1,
                                }}
                            />
                        </div>
                        {/* Cancel */}
                        <div
                            style={{ ...circleButtonBase, backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }}
                            onClick={() => { setEditingRole(null); setEditRoleName(''); }}
                        >
                            <CloseOutlined style={{ fontSize: ACTION_ICON_SIZE, color: '#6b7280' }} />
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            style={{
                                backgroundColor: role.color,
                                marginRight: 'calc(var(--sp-md2)*1.132075)',
                                fontWeight: 'bold',
                                fontSize: 'calc(var(--fs-xs)*1.50943)',
                                height: 'calc(var(--fs-xl)*1.50943)',
                                width: 'calc(var(--fs-xl)*1.50943)',
                            }}
                        >
                            {text.charAt(0)}
                        </Avatar>
                        <TextBase size={'calc(var(--fs-xs)*1.50943)'} color={Colors.titlePage}>
                            {text}
                        </TextBase>
                    </div>
                ),
        },

        /* ── USERS column ── */
        {
            title: 'USERS',
            dataIndex: 'users',
            key: 'users',
            width: '15%',
            align: 'center',
            render: (users) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Tag
                        color="blue"
                        style={{ ...pillTagStyle, padding: '0 calc(var(--sp-md2)*1.2)', width: 'calc(var(--fs-hero)*1.886792)' }}
                    >
                        {users} Users
                    </Tag>
                </div>
            ),
        },

        /* ── PERMISSIONS column ── */
        {
            title: 'PERMISSIONS',
            dataIndex: 'permissions',
            key: 'permissions',
            width: '40%',
            render: (permissions = []) => {
                if (!Array.isArray(permissions)) return null;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'calc(var(--sp-xxs)*2)', alignItems: 'center', minHeight: 'calc(var(--fs-xl)*1.6)' }}>
                        {permissions.slice(0, 3).map((perm) => (
                            <Tag key={perm.id || perm} color="geekblue" style={{ ...pillTagStyle, padding: '0 calc(var(--sp-md2)*1.1)' }}>
                                {perm.name}
                            </Tag>
                        ))}
                        {permissions.length > 3 && (
                            <Tag color="purple" style={{ ...pillTagStyle, padding: '0 calc(var(--sp-md2)*1.1)' }}>
                                +{permissions.length - 3} more
                            </Tag>
                        )}
                    </div>
                );
            },
        },

        /* ── STATUS column ── */
        {
            title: 'STATUS',
            key: 'status',
            width: '10%',
            render: (_, role) => {
                const isActive = role.users > 0;
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Tag
                            style={{
                                ...pillTagStyle,
                                padding: '0 calc(var(--sp-md2)*1.3)',
                                fontWeight: 600,
                                margin: 0,
                                width: 'calc(var(--fs-hero)*1.886792)',
                                backgroundColor: isActive ? '#f6ffed' : '#fff1f0',
                                color: isActive ? '#389e0d' : '#cf1322',
                                border: isActive ? '1px solid #b7eb8f' : '1px solid #ffa39e',
                            }}
                        >
                            {isActive ? 'Active' : 'Inactive'}
                        </Tag>
                    </div>
                );
            },
        },

        /* ── ACTIONS column ── */
        {
            title: 'ACTIONS',
            key: 'actions',
            width: '15%',
            align: 'center',
            render: (_, role) => {
                const isDeleteDisabled = role.is_system || role.users > 0;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'calc(var(--sp-md2)*1.2)' }}>
                        {/* Edit */}
                        <Tooltip title="Edit Role" placement="top" overlayInnerStyle={tooltipOverlayStyle}>
                            <Button
                                type="text"
                                onClick={() => handleEditRole(role)}
                                style={actionBtnStyle}
                                icon={<EditOutlined style={{ fontSize: ACTION_ICON_SIZE }} />}
                            />
                        </Tooltip>

                        {/* Permissions */}
                        <Tooltip title="Manage Permissions" placement="top" overlayInnerStyle={tooltipOverlayStyle}>
                            <Button
                                type="text"
                                onClick={() => openAssignModal(role)}
                                style={actionBtnStyle}
                                icon={<SafetyCertificateOutlined style={{ fontSize: ACTION_ICON_SIZE }} />}
                            />
                        </Tooltip>

                        {/* Delete */}
                        <Tooltip
                            title={role.users > 0 ? 'Cannot delete role with users assigned' : 'Delete Role'}
                            placement="top"
                            overlayInnerStyle={tooltipOverlayStyle}
                        >
                            <Button
                                type="text"
                                danger
                                disabled={isDeleteDisabled}
                                loading={deletingRoleId === role.id}
                                onClick={() => { setRoleToDelete(role); setShowDeleteRoleModal(true); }}
                                style={{
                                    ...actionBtnStyle,
                                    opacity: isDeleteDisabled ? 0.6 : 1,
                                    cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
                                }}
                                icon={<DeleteOutlined style={{ fontSize: ACTION_ICON_SIZE }} />}
                            />
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <RoleBox>
            {/* Header Section */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--sp-lg)'
            }}>
                <div>
                    <PageTitleHeader
                        title="Role Management"
                        subtitle='Create and manage roles with specific permissions'
                        containerStyle={{ marginBottom: 'var(--sp-lg)' }}
                    />
                </div>
                <CreateButton
                    onClick={() => {
                        setNewRole('');
                        setNewRolePermissions([]);   // ⭐ reset always
                        setCreateModalVisible(true);
                    }}
                    buttonColor={buttonColor}
                    whiteLabel={whiteLabel}
                >
                    <CreatePlusIcon style={{ width: 'calc(var(--fs-xs)*1.226415)', height: 'calc(var(--fs-xs)*1.226415)', display: 'inline-flex' }} />
                    <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 'calc(var(--fs-xs)*0.09433)', fontWeight: 600 }}>{translate('createRole')}</span>
                </CreateButton>
            </div>

            <Row justify="space-between" align="middle" style={{ marginBottom: 'calc(var(--sp-lg)*1.50943)' }}>
                <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
                    <Search
                        placeholder="Search roles..."
                        setQuery={setSearchText}
                        value={searchText}
                        // sx={{ width: 250, borderRadius: 8 }}
                        sx={{
                            width: 'calc(var(--fs-hero)*5)',
                            '& .MuiOutlinedInput-input': {
                                fontSize: 'var(--fs-md2) !important',
                                padding: 'var(--sp-md2) var(--sp-md2*1.4)',
                            },
                            '& .MuiOutlinedInput-input::placeholder': {
                                fontSize: 'var(--fs-md2)',
                                padding: 'var(--sp-md2) var(--sp-md2*1.4)',
                                lineHeight: '100%',
                            },
                        }}
                    />
                </Col>
                <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center">
                        <MuiButton
                            ref={anchorRef}
                            onClick={() => setOpenSortByMenu(prev => !prev)}
                            sx={{
                                textTransform: 'capitalize',
                                fontWeight: 700,
                                height: 'calc(var(--fs-xl)*2.35) !important',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                borderRadius: 'var(--sp-md2)',
                                fontSize: 'calc(var(--fs-sm2) * 1.015)',
                                backgroundColor: '#ffffff !important',
                                border: '1.5px solid #e5e7eb !important',
                                color: '#374151 !important',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                padding: 'calc(var(--sp-md)*1) calc(var(--sp-md2)*1.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'calc(var(--sp-xxs)*1.5)',
                                '&:hover': {
                                    backgroundColor: '#f3f4f6 !important',
                                    borderColor: '#d1d5db !important',
                                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                                    transform: 'translateY(-2px)'
                                },
                                '&:active': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)'
                                }
                            }}>
                            <span style={{ fontWeight: 500, color: '#374151' }}>Show:</span>
                            <span style={{ fontWeight: 600, color: '#1A5A78' }}>
                                {ROLE_SORT_OPTIONS.find(o => o.value === statusFilter)?.label}
                            </span>
                            <ArrowDropDownIcon
                                sx={{
                                    fontSize: 'calc(var(--fs-xl) * 1.132075)',
                                    marginLeft: 'auto',
                                    transition: 'transform 0.2s ease',
                                    transform: openSortByMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                                    color: '#9ca3af'
                                }}
                            />
                        </MuiButton>

                        <Popper
                            open={openSortByMenu}
                            anchorEl={anchorRef.current}
                            placement="bottom-start"
                            transition
                            disablePortal
                            strategy="fixed"
                            sx={{ zIndex: 9999, textTransform: 'capitalize' }}
                            modifiers={[
                                {
                                    name: 'sameWidth',
                                    enabled: true,
                                    phase: 'beforeWrite',
                                    requires: ['computeStyles'],
                                    fn: ({ state }) => {
                                        state.styles.popper.width = `${state.rects.reference.width}px`;
                                    },
                                },
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        altBoundary: true,
                                        tether: false,
                                        padding: 8,
                                    },
                                },
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 4],
                                    },
                                },
                            ]}
                        >
                            {({ TransitionProps, placement }) => (
                                <Grow {...TransitionProps} style={{
                                    transformOrigin:
                                        placement === 'bottom' ? 'center top' : 'center bottom'
                                }}>
                                    <Paper sx={{ width: '100%' }}>
                                        <ClickAwayListener onClickAway={() => setOpenSortByMenu(false)}>
                                            <MenuList autoFocusItem>
                                                {ROLE_SORT_OPTIONS.map(option => (
                                                    <MenuItem
                                                        key={option.value}
                                                        selected={option.value === statusFilter}
                                                        onClick={() => {
                                                            setStatusFilter(option.value);
                                                            setOpenSortByMenu(false);
                                                        }}
                                                        style={{
                                                            color: option.value === statusFilter ? '#1A5A78' : '#000',
                                                            fontSize: 'var(--fs-md2)',
                                                            fontWeight: option.value === statusFilter ? '600' : '500'
                                                        }}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </MenuList>
                                        </ClickAwayListener>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                    </Box>
                </Col>
            </Row>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 'calc(var(--sp-lg)*1.50943)' }}>
                {[
                    {
                        label: 'Total Roles',
                        value: totalRoles,
                        icon: <ManageAccountsOutlined sx={{ fontSize: 'calc(var(--fs-xl)*1.50943)', color: '#7c3aed' }} />,
                        bgColor: '#f3f0ff'
                    },
                    {
                        label: 'Active Roles',
                        value: roles.filter(r => r.users > 0).length,
                        icon: <AdminPanelSettingsOutlined sx={{ fontSize: 'calc(var(--fs-xl)*1.50943)', color: '#16a34a' }} />,
                        bgColor: '#ecfdf5'
                    },
                    {
                        label: 'Permissions Available',
                        value: totalPermissions,
                        icon: <SecurityOutlined sx={{ fontSize: 'calc(var(--fs-xl)*1.50943)', color: '#ea580c' }} />,
                        bgColor: '#fff7ed'
                    }
                ].map((stat, idx) => (
                    <Col span={8} key={idx}>
                        <Card
                            style={{
                                borderRadius: 'calc(var(--sp-md2)*0.8)',
                                border: '1px solid #f0f0f0',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                height: '100%' // important
                            }}
                            bodyStyle={{
                                padding: 'calc(var(--sp-md2)*1.2) calc(var(--sp-md2)*1.6)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'calc(var(--sp-md2)*1)',
                                height: '100%',             // important
                                minHeight: 'calc(var(--fs-hero)*2.1226415)'           // control visual balance
                            }}
                        >
                            <div style={{
                                width: 'calc(var(--fs-hero)*1.50943)',
                                height: 'calc(var(--fs-hero)*1.50943)',
                                borderRadius: 'calc(var(--sp-xs)*1.2)',
                                backgroundColor: stat.bgColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                {stat.icon}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    flex: 1
                                }}
                            >
                                <div style={{
                                    fontSize: 'calc(var(--fs-xl)*1)',
                                    fontWeight: 700,
                                    color: '#111827',
                                    lineHeight: 1.2
                                }}>
                                    {stat.value}
                                </div>
                                <div style={{
                                    fontSize: 'calc(var(--fs-xs)*1.50943)',
                                    color: '#6b7280',
                                    fontWeight: 500,
                                    marginTop: 'calc(var(--sp-md2)*0.09433)'
                                }}>
                                    {stat.label}
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Roles Table */}
            <Card
                style={{
                    borderRadius: 'calc(var(--sp-md2)*1.132075)',
                }}
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    dataSource={filteredRoles}
                    rowKey={record => record.id}
                    columns={columns}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    loading={{ spinning: isRoleLoading, indicator: <CustomSpinner fullPage /> }}
                />
            </Card>
            {!isRoleLoading && paging}

            {/* ── Assign Permissions Modal ── */}
            <Modal
                title={`Assign Permissions${selectedRole?.name ? ': ' + selectedRole.name : ''}`}
                isOpen={assignModalVisible}
                onClose={() => setAssignModalVisible(false)}
                showHeader={true}
                isIcon={true}
                width={'calc(var(--fs-hero)*17.6886792)'}
                Icon={
                    <SafetyCertificateOutlined
                        style={{
                            fontSize: 'calc(var(--fs-xl)*1.132075)',
                            marginRight: 'calc(var(--sp-md2)*1.132075)',
                            color: '#1890ff',
                        }}
                    />
                }
            >
                <PermissionScrollContent
                    isLoading={roleDetailLoading}
                    permissionsList={permissionsList}
                    value={selectedPermissions}
                    onChange={setSelectedPermissions}
                    permissionLoading={permissionLoading}
                    onScroll={handlePermissionScroll}
                    title="Select permissions for this role"
                />
                <ModalActionFooter
                    onCancel={() => setAssignModalVisible(false)}
                    onSubmit={handleSavePermissions}
                    loading={savingPermissions}
                    disabled={selectedPermissions.length === 0}
                    buttonColor={buttonColor}
                    submitText="Save Permissions"
                />
            </Modal>

            {/* ── Create Role Modal ── */}
            <Modal
                title="Create New Role"
                isOpen={createModalVisible}
                onClose={() => {
                    setCreateModalVisible(false);
                    setNewRole('');
                    setNewRolePermissions([]);
                }}
                width={'calc(var(--fs-hero)*17.6886792)'}
            >
                <Label htmlFor="newRoleInput">Role Name</Label>
                <TextField
                    value={newRole}
                    type="text"
                    id="newRoleInput"
                    placeholder="Enter role name"
                    onChange={(e) => setNewRole(e.target.value)}
                    style={{
                        marginBottom: 'calc(var(--sp-md2)*2.5)',
                        borderRadius: 8,
                        marginTop: 'var(--sp-md2)',
                    }}
                    name="newRole"
                    fullWidth
                    InputProps={{
                        sx: {
                            '& .MuiInputBase-input': {
                                fontSize: 'var(--fs-md2) !important',
                                fontWeight: 400,
                            },
                        },
                    }}
                />
                <PermissionScrollContent
                    isLoading={roleDetailLoading}
                    permissionsList={permissionsList}
                    value={newRolePermissions}
                    onChange={setNewRolePermissions}
                    permissionLoading={permissionLoading}
                    onScroll={handlePermissionScroll}
                    title="Manage Permissions"
                    titleMarginBottom="calc(var(--sp-xs)*3)"
                />
                <ModalActionFooter
                    onCancel={() => setCreateModalVisible(false)}
                    onSubmit={handleCreateRoleFromModal}
                    loading={creatingRole}
                    disabled={creatingRole || !newRole.trim() || newRolePermissions.length === 0}
                    buttonColor={buttonColor}
                    submitText="Create"
                />
            </Modal>

            {/* ── Delete Confirmation Dialog ── */}
            <ConfirmDialog
                isOpen={showDeleteRoleModal}
                onClose={() => {
                    setShowDeleteRoleModal(false);
                    setRoleToDelete(null);
                }}
                title='deleteRole'
                description='deleteRoleConfirmText'
                onOk={() => {
                    if (roleToDelete) {
                        handleDeleteRole(roleToDelete);
                    }
                }}
                loading={deletingRoleId === roleToDelete?.id}
            />
        </RoleBox>
    );
};

export default RoleManagement;